if (!customElements.get("ona-slider")) {
  class FlickingWheelSlide {
    set horizontal(value) {
      this._horizontal = value;
    }
    get horizontal() {
      return this._horizontal;
    }
    /**
     * Create a new WheelSlide plugin
     * @param {boolean} horizontal - Scroll direction, default is false
     * @example
     * ```ts
     * import WheelSlide from "@/plugins/flicking/WheelSlide";
     * flicking.addPlugins(new WheelSlide(true|false));
     * ```
     */
    constructor(horizontal = false) {
      this._handleWheel = this._handleWheel.bind(this);
      this._flicking = null;
      this._horizontal = horizontal;
      this._lastTime = performance.now();
      this._velocity = 0;
      this._inertiaAnim = null;
      this._inertiaTimeout = null;
      this._friction = 0.95;
      this._threshold = 0.1;
    }
    init(flicking) {
      if (this._flicking) this.destroy();
      this._flicking = flicking;
      this._el = flicking.element;
      this._el.addEventListener("wheel", this._handleWheel, { passive: false });
    }
    startInertia(initialVelocity) {
      let currentVelocity = initialVelocity;
      const animate = () => {
        currentVelocity *= this._friction;
        const cameraPos = this._flicking.camera.position;
        this._flicking.control.controller.axes.setTo({ flick: cameraPos + currentVelocity }, 0);
        if (Math.abs(currentVelocity) > this._threshold) {
          this._inertiaAnim = requestAnimationFrame(animate);
        } else {
          this._inertiaAnim = null;
        }
      };
      animate();
    }
    _handleWheel(e) {
      const cameraPos = this._flicking.camera.position;
      const scrollDelta = this._horizontal ? e.deltaX : e.deltaY;
      const now = performance.now();
      const deltaTime = now - this._lastTime;
      this._lastTime = now;
      this._velocity = scrollDelta / deltaTime;
      this._flicking.control.controller.axes.setTo({ flick: cameraPos + scrollDelta / 2 }, 0);
      if (this._inertiaAnim) {
        cancelAnimationFrame(this._inertiaAnim);
        this._inertiaAnim = null;
      }
      clearTimeout(this._inertiaTimeout);
      this._inertiaTimeout = setTimeout(() => {
        this.startInertia(this._velocity);
      }, 50);
    }
    destroy() {
      if (!this._flicking) return;
      this._flicking = null;
      this._el.removeEventListener("wheel", this._handleWheel);
    }
  }
  customElements.define(
    "ona-slider",
    class Slider extends HTMLElement {
      constructor() {
        super();
      }
      connectedCallback() {
        this.elements = {
          viewport: this.querySelector(".slider__viewport"),
          items: this.querySelector(".slider__items"),
          prevButton: this.querySelector(".slider__button--prev"),
          nextButton: this.querySelector(".slider__button--next"),
          dots: this.querySelector(".slider__dots")
        };
        this.prev = this.prev.bind(this);
        this.next = this.next.bind(this);
        this._updateDots = this._updateDots.bind(this);
        this._updateArrows = this._updateArrows.bind(this);
        this.align = this.getAttribute("align") || "center";
        this.mobileAlign = this.getAttribute("mobile-align") || "center";
        this.isVertical = this.getAttribute("vertical") !== null;
        this.isBound = this.getAttribute("bound") !== null;
        if (!this.elements.viewport || !this.elements.items) {
          console.error("Slider: Required elements not found.");
          return;
        }
        this.elements.viewport.classList.add("flicking-viewport");
        this.elements.items.classList.add("flicking-camera");
        if (this.isVertical) {
          this.elements.viewport.classList.add("vertical");
        }
        if (this.align[0] === "{") {
          this.align = JSON.parse(this.align);
        }
        if (this.mobileAlign[0] === "{") {
          this.mobileAlign = JSON.parse(this.mobileAlign);
        }
        const isMobile = window.innerWidth <= 768;
        const deviceAlign = isMobile ? this.mobileAlign : this.align;
        const deviceBound = isMobile ? false : this.isBound;
        this.flicking = new Flicking(this.elements.viewport, {
          align: deviceAlign,
          horizontal: !this.isVertical,
          bound: deviceBound
        });
        this.flicking.addPlugins(new FlickingWheelSlide(true));
        if (this.elements.prevButton) {
          this.elements.prevButton.addEventListener("click", this.prev);
        }
        if (this.elements.nextButton) {
          this.elements.nextButton.addEventListener("click", this.next);
        }
        if (this.elements.dots) {
          this.flicking.on("willChange", this._updateDots);
          this.flicking.on("changed", this._updateDots);
        }
        this.flicking.on("ready", this._updateArrows);
        this.flicking.on("visibleChange", this._updateArrows);
        this.flicking.on("changed", () => {
          this.dispatchEvent(
            new CustomEvent("slideChanged", {
              detail: { index: this.flicking.index, currentSlide: this.flicking.currentPanel }
            })
          );
        });
      }
      prev() {
        if (this.flicking.index > 0) this.flicking.prev();
      }
      next() {
        if (this.flicking.index < this.flicking.panelCount - 1) this.flicking.next();
      }
      moveTo(index) {
        if (this.flicking.animating) {
          this.flicking.updateAnimation(index, this.flicking.duration);
        } else {
          this.flicking.moveTo(index);
        }
      }
      _updateDots(event) {
        this.elements.dots.querySelectorAll(".slider__dot").forEach((dot) => {
          dot.ariaSelected = false;
        });
        this.elements.dots.querySelector(`.slider__dot:nth-child(${event.index + 1})`).ariaSelected = true;
      }
      _updateArrows() {
        var _a;
        if (!((_a = this.flicking.visiblePanels) == null ? void 0 : _a.length)) return;
        const isFirstPanelVisible = this.flicking.visiblePanels[0].index === 0 && this.flicking.visiblePanels[0].visibleRatio === 1;
        const isLastPanelVisible = this.flicking.visiblePanels.at(-1).index === this.flicking.panelCount - 1 && this.flicking.visiblePanels.at(-1).visibleRatio === 1;
        if (this.elements.prevButton) this.elements.prevButton.disabled = isFirstPanelVisible;
        if (this.elements.nextButton) this.elements.nextButton.disabled = isLastPanelVisible;
        if (this.elements.prevButton && this.elements.nextButton) {
          if (isFirstPanelVisible && isLastPanelVisible) {
            this.elements.prevButton.style.display = "none";
            this.elements.nextButton.style.display = "none";
          } else {
            this.elements.prevButton.style.display = "";
            this.elements.nextButton.style.display = "";
          }
        }
      }
    }
  );
}
